import { instance } from '../../requests/axios.instance';
import { documentBaseUrl } from '../../requests/documentDatas';
import { uploadDirect } from '../../requests/directUpload';
import { normalizeBankMedia } from '../../shared/media/mediaBank';
import extractVideoPoster from '../media/videoPoster';

/** Contexte éditeur (proposal ou web-template). */
function getEditorContext() {
  const path = window.location.pathname || '';
  const search = new URLSearchParams(window.location.search || '');

  const proposalMatch = path.match(/^\/web-editor\/(\d+)/);
  if (proposalMatch) {
    return { type: 'proposal', id: proposalMatch[1] };
  }

  if (path.startsWith('/web-templates')) {
    const templateId = search.get('edit');
    if (templateId) return { type: 'web_template', id: templateId };
  }

  return null;
}

export function canUploadImageInCurrentContext() {
  const ctx = getEditorContext();
  return !!ctx?.id;
}

function imageUploadUrl(ctx) {
  return ctx.type === 'proposal'
    ? `/proposal/${ctx.id}/image`
    : `/proposals/web_templates/${ctx.id}/images`;
}

function videoUploadUrl(ctx) {
  return ctx.type === 'proposal'
    ? `/proposal/${ctx.id}/video`
    : `/proposals/web_templates/${ctx.id}/videos`;
}

/**
 * Réponse d'upload ramenée à la forme d'un média de la banque.
 *
 * Un back qui ne renvoie encore que `{ url }` reste exploitable : le média n'a
 * alors ni palier ni `srcset`, et le champ se comporte comme avant.
 */
function mediaFromUploadResponse(data) {
  const media = normalizeBankMedia(data);
  if (!media) throw new Error('Réponse upload invalide : URL du média absente.');
  return media;
}

/**
 * Envoi d'un fichier vers la banque.
 *
 * Le fichier part **directement** au stockage (`DirectUpload`) et seul son
 * `signed_id` est posté : au-delà de quelques mégaoctets le base64 ne tient
 * plus — le corps de requête est un tiers plus lourd et passe entièrement en
 * mémoire côté Rails.
 */
export async function uploadWebImage(file, { onProgress } = {}) {
  const ctx = getEditorContext();
  if (!ctx?.id) {
    throw new Error('Sauvegardez d\'abord cet élément pour pouvoir uploader une image.');
  }

  const signedId = await uploadDirect(file, onProgress);
  const res = await instance.post(imageUploadUrl(ctx), {
    filename: file.name,
    signed_id: signedId,
  });

  return mediaFromUploadResponse(res?.data);
}

/**
 * Envoi d'une vidéo.
 *
 * Le poster est extrait **dans le navigateur** (première frame) et envoyé à
 * côté du fichier : ffmpeg n'est pas disponible côté serveur, ActiveStorage ne
 * sait donc pas prévisualiser une vidéo. Une extraction qui échoue ne bloque
 * pas l'envoi — la vidéo est simplement sans vignette.
 */
export async function uploadWebVideo(file, { onProgress } = {}) {
  const ctx = getEditorContext();
  if (!ctx?.id) {
    throw new Error('Sauvegardez d\'abord cet élément pour pouvoir uploader une vidéo.');
  }

  const poster = await extractVideoPoster(file);
  const signedId = await uploadDirect(file, onProgress);
  const posterSignedId = poster ? await uploadDirect(poster) : null;

  const res = await instance.post(videoUploadUrl(ctx), {
    filename: file.name,
    signed_id: signedId,
    ...(posterSignedId ? { poster_signed_id: posterSignedId } : {}),
  });

  return mediaFromUploadResponse(res?.data);
}

/**
 * Banque de médias du document, **sans avaler les erreurs**.
 *
 * `getMediaBank` (`requests/documentDatas.js`) rend `[]` sur échec : l'éditeur
 * classique s'en accommode, le web-editor non. Une banque vide et un endpoint
 * absent ne se disent pas pareil à l'utilisateur. Le picker doit pouvoir dire
 * « indisponible » plutôt que « aucun média ».
 */
export async function fetchMediaBank({ id, type }) {
  if (!id || !type) throw new Error('contexte de banque de médias invalide');

  const res = await instance.get(`${documentBaseUrl({ id, type })}media_bank`);
  if (res.status !== 200) throw new Error(`GET media bank status code ${res.status}`);
  return Array.isArray(res.data) ? res.data : [];
}
